import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Pencil, Plus, Trash2, X } from "lucide-react";
import { ImageUpload } from "@/components/admin/ImageUpload";
import {
  deleteEcosystemPartner,
  upsertEcosystemPartner,
  type EcosystemPartner,
  type EcosystemPartnerInput,
} from "@/lib/ecosystem-partners.functions";
import { adminEcosystemPartnersQuery, INVALIDATE } from "@/lib/query-registry";
import { useAuthedMutation } from "@/lib/authed-mutation";

export const Route = createFileRoute("/admin/ecosystem")({
  component: EcosystemAdminPage,
});

type FormState = {
  id?: string;
  name: string;
  slug: string;
  category: string;
  description: string;
  website_url: string;
  logo_url: string | null;
  published: boolean;
  nofollow: boolean;
  sponsored: boolean;
  sort_order: number;
};

const EMPTY_FORM: FormState = {
  name: "",
  slug: "",
  category: "Technology partner",
  description: "",
  website_url: "",
  logo_url: null,
  published: true,
  nofollow: true,
  sponsored: true,
  sort_order: 0,
};

const inputClass =
  "mt-2 w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-sm text-foreground outline-none focus:border-foreground";

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 120);
}

function fromPartner(partner: EcosystemPartner): FormState {
  return {
    id: partner.id,
    name: partner.name,
    slug: partner.slug,
    category: partner.category,
    description: partner.description,
    website_url: partner.website_url,
    logo_url: partner.logo_url,
    published: partner.published,
    nofollow: partner.nofollow,
    sponsored: partner.sponsored,
    sort_order: partner.sort_order,
  };
}

function EcosystemAdminPage() {
  const query = useQuery(adminEcosystemPartnersQuery());
  const partners = (query.data?.partners ?? []) as EcosystemPartner[];
  const storageReady = query.data?.storageReady ?? false;
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);

  const saveMutation = useAuthedMutation<EcosystemPartnerInput, { id: string }>(
    upsertEcosystemPartner,
    {
      invalidate: [INVALIDATE.adminEcosystemPartners, INVALIDATE.publicEcosystemPartners],
      onSuccess: () => {
        setForm(EMPTY_FORM);
        setShowForm(false);
      },
    },
  );

  const deleteMutation = useAuthedMutation<{ id: string }, { ok: boolean }>(
    deleteEcosystemPartner,
    {
      invalidate: [INVALIDATE.adminEcosystemPartners, INVALIDATE.publicEcosystemPartners],
    },
  );

  const edit = (partner: EcosystemPartner) => {
    if (!storageReady) return;
    setForm(fromPartner(partner));
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = (partner: EcosystemPartner) => {
    if (!storageReady || !partner.id || partner.id === "ugc-king-fallback") return;
    if (!confirm(`Delete ${partner.name} from the partner directory?`)) return;
    deleteMutation.mutate({ id: partner.id });
  };

  const save = (event: React.FormEvent) => {
    event.preventDefault();
    if (!storageReady) return;
    saveMutation.mutate({
      ...form,
      slug: form.slug || slugify(form.name),
      logo_url: form.logo_url || null,
    });
  };

  const errorMessage =
    query.error?.message ?? saveMutation.error?.message ?? deleteMutation.error?.message ?? null;

  return (
    <div className="pb-16">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Public directory
          </p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight text-foreground">
            Partner ecosystem
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
            Manage the logos, descriptions, destination URLs, link attributes, publication state,
            and display order used on /partners.
          </p>
        </div>
        {storageReady && !showForm && (
          <button
            type="button"
            onClick={() => {
              setForm({ ...EMPTY_FORM, sort_order: partners.length });
              setShowForm(true);
            }}
            className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background hover:opacity-90"
          >
            <Plus className="h-4 w-4" />
            Add partner
          </button>
        )}
      </div>

      {!storageReady && !query.isLoading && (
        <div className="mt-7 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5 text-sm leading-6 text-foreground">
          <strong>Partner storage is not installed yet.</strong> The public page is protected by a
          built-in UGC King fallback, but editing stays locked until the
          <code className="mx-1 rounded bg-background px-1.5 py-0.5 text-xs">
            ecosystem_partners
          </code>
          migration is applied once. After that, future partner changes can be made entirely here.
        </div>
      )}

      {errorMessage && (
        <p className="mt-6 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {errorMessage}
        </p>
      )}

      {showForm && storageReady && (
        <form
          onSubmit={save}
          className="mt-8 rounded-2xl border border-border bg-card/30 p-6 md:p-8"
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-display text-xl font-semibold text-foreground">
                {form.id ? `Edit ${form.name}` : "Add partner"}
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                External link qualification defaults to nofollow + sponsored.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setForm(EMPTY_FORM);
                setShowForm(false);
              }}
              className="rounded-full border border-border p-2 text-muted-foreground hover:text-foreground"
              aria-label="Close form"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-7 grid gap-6 md:grid-cols-2">
            <label className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
              Name
              <input
                className={inputClass}
                value={form.name}
                onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                required
                maxLength={120}
              />
            </label>

            <label className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
              Slug
              <input
                className={inputClass}
                value={form.slug}
                onChange={(e) =>
                  setForm((current) => ({ ...current, slug: slugify(e.target.value) }))
                }
                placeholder={slugify(form.name) || "partner-name"}
                maxLength={120}
              />
            </label>

            <label className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
              Category
              <input
                className={inputClass}
                value={form.category}
                onChange={(e) => setForm((current) => ({ ...current, category: e.target.value }))}
                required
                maxLength={100}
              />
            </label>

            <label className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
              Website URL
              <input
                type="url"
                className={inputClass}
                value={form.website_url}
                onChange={(e) =>
                  setForm((current) => ({ ...current, website_url: e.target.value }))
                }
                placeholder="https://partner.com"
                required
              />
            </label>
          </div>

          <label className="mt-6 block text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Description
            <textarea
              className={`${inputClass} min-h-32 resize-y normal-case tracking-normal`}
              value={form.description}
              onChange={(e) =>
                setForm((current) => ({ ...current, description: e.target.value }))
              }
              required
              maxLength={1200}
            />
          </label>

          <div className="mt-6">
            <ImageUpload
              value={form.logo_url}
              onChange={(url) => setForm((current) => ({ ...current, logo_url: url }))}
              label="Partner logo"
              pathPrefix="partners/"
            />
          </div>

          <div className="mt-7 grid gap-5 rounded-xl border border-border bg-background p-5 sm:grid-cols-2 lg:grid-cols-4">
            <label className="flex items-center gap-3 text-sm text-foreground">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) =>
                  setForm((current) => ({ ...current, published: e.target.checked }))
                }
                className="h-4 w-4"
              />
              Published
            </label>

            <label className="flex items-center gap-3 text-sm text-foreground">
              <input
                type="checkbox"
                checked={form.nofollow}
                onChange={(e) =>
                  setForm((current) => ({ ...current, nofollow: e.target.checked }))
                }
                className="h-4 w-4"
              />
              nofollow
            </label>

            <label className="flex items-center gap-3 text-sm text-foreground">
              <input
                type="checkbox"
                checked={form.sponsored}
                onChange={(e) =>
                  setForm((current) => ({ ...current, sponsored: e.target.checked }))
                }
                className="h-4 w-4"
              />
              sponsored
            </label>

            <label className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
              Sort order
              <input
                type="number"
                min={0}
                max={10000}
                className="mt-1.5 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                value={form.sort_order}
                onChange={(e) =>
                  setForm((current) => ({
                    ...current,
                    sort_order: Number(e.target.value) || 0,
                  }))
                }
              />
            </label>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="rounded-full bg-foreground px-6 py-2.5 text-sm font-semibold text-background disabled:opacity-50"
            >
              {saveMutation.isPending ? "Saving…" : form.id ? "Save partner" : "Add partner"}
            </button>
            <button
              type="button"
              onClick={() => {
                setForm(EMPTY_FORM);
                setShowForm(false);
              }}
              className="rounded-full border border-border px-5 py-2.5 text-sm text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="mt-8 overflow-hidden rounded-2xl border border-border bg-background">
        <div className="grid grid-cols-[1fr_120px_120px] gap-4 bg-card/60 px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground md:grid-cols-[80px_1fr_170px_120px_160px]">
          <span className="hidden md:block">Logo</span>
          <span>Partner</span>
          <span className="hidden md:block">Category</span>
          <span>Status</span>
          <span className="text-right">Actions</span>
        </div>

        {query.isLoading ? (
          <p className="border-t border-border px-5 py-8 text-sm text-muted-foreground">
            Loading partners…
          </p>
        ) : partners.length === 0 ? (
          <p className="border-t border-border px-5 py-8 text-sm text-muted-foreground">
            No partners yet.
          </p>
        ) : (
          partners.map((partner) => (
            <div
              key={partner.id}
              className="grid grid-cols-[1fr_120px_120px] items-center gap-4 border-t border-border px-5 py-4 md:grid-cols-[80px_1fr_170px_120px_160px]"
            >
              <div className="hidden md:block">
                {partner.logo_url ? (
                  <img
                    src={partner.logo_url}
                    alt=""
                    width={64}
                    height={44}
                    className="h-11 w-16 rounded-lg border border-border object-contain p-1"
                  />
                ) : (
                  <div className="flex h-11 w-16 items-center justify-center rounded-lg border border-border bg-card text-[10px] font-semibold">
                    {partner.name.slice(0, 3).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <p className="truncate font-medium text-foreground">{partner.name}</p>
                <a
                  href={partner.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-flex max-w-full items-center gap-1 truncate text-xs text-muted-foreground hover:text-foreground"
                >
                  {partner.website_url.replace(/^https?:\/\//, "")}
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </a>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {partner.nofollow ? "nofollow" : "follow"}
                  {partner.sponsored ? " · sponsored" : ""}
                  {" · order "}
                  {partner.sort_order}
                </p>
              </div>
              <p className="hidden truncate text-sm text-muted-foreground md:block">
                {partner.category}
              </p>
              <span
                className={`w-fit rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                  partner.published
                    ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {partner.published ? "Published" : "Draft"}
              </span>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => edit(partner)}
                  disabled={!storageReady}
                  className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => remove(partner)}
                  disabled={!storageReady || partner.id === "ugc-king-fallback"}
                  className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:border-destructive hover:text-destructive disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Trash2 className="h-3 w-3" />
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
